import * as React from 'react';
import BasicDialog, {BasicDialogProps} from 'cl-dialogs/BasicDialog';
import {reduxForm, DecoratedComponentClass, InjectedFormProps, ConfigProps} from 'redux-form';
import FormFields, {ExtendedWrappedFieldExternalProps} from 'cl-form/FormFields';
import Alert from '@material-ui/core/Alert/Alert';
import {Omit, StepLabel} from '@material-ui/core';
import * as _ from 'lodash';
import Stepper, {StepperProps} from '@material-ui/core/Stepper/Stepper';
import Step from '@material-ui/core/Step/Step';
import Button from '@material-ui/core/Button/Button';
import {createValidateRequired} from 'cl-form/validation/validators';
import {PaperProps} from '@material-ui/core/Paper/Paper';
import {stringToDashed} from 'cl-automation-utils';

export type StepInfo<FormData> = {name: string; validate?: ConfigProps<FormData>['validate']};

export interface FormDialogState {
  step: StepperProps['activeStep'];
}

export type Fields<FormData extends object = {}> = ExtendedWrappedFieldExternalProps<keyof FormData>[];

export interface FormDialogProps<FormData extends object = {}> extends Omit<BasicDialogProps, 'onChange' | 'onSubmit'> {
  formRef?: any;
  formName: string;
  disableExit?: boolean;
  steps?: StepInfo<FormData>[];
  fields?: Fields<FormData> | {[stepName: string]: ExtendedWrappedFieldExternalProps<keyof FormData>[]};
}

export type SpecificFormDialogProps<FormData extends object = {}, T extends object = {}> = Omit<
  FormDialogComputedProps<FormData> & T,
  'fields' | 'actions'
>;

interface WrappedComponentInternalProps extends FormDialogState {
  setStep: (step: FormDialogState['step']) => () => void;
  hasMoreSteps: boolean;
  clearSubmitErrors?: () => any; // clearSubmitErrors is an undocumented (and not included in @types
}

export type FormDialogComputedProps<FormData extends object = {}> = FormDialogProps<FormData> &
  Partial<ConfigProps<FormData, FormDialogProps<FormData>>>;
export default class FormDialog<FormData extends object = {}> extends React.PureComponent<
  FormDialogComputedProps<FormData>,
  FormDialogState
> {
  Wrapped: DecoratedComponentClass<
    FormData,
    FormDialogProps<FormData> &
      WrappedComponentInternalProps &
      Partial<ConfigProps<FormData, FormDialogProps<FormData>>>,
    string
  >;

  constructor(props) {
    super(props);
    const {text, onClose, formName, disableExit, fullWidth, maxWidth} = props;
    this.state = {
      step: 0,
    };

    // the call to reduxForm must be done once per dialog and not during render.
    class Inner extends React.PureComponent<
      FormDialogProps<FormData> &
        InjectedFormProps<FormData, FormDialogProps<FormData> & WrappedComponentInternalProps> &
        WrappedComponentInternalProps
    > {
      render() {
        const {
          setStep,
          step,
          steps,
          fields,
          anchorEl,
          onExited,
          open,
          title,
          submitting,
          clearSubmitErrors,
          error,
          actions,
          handleSubmit,
          submitFailed,
          content,
        } = this.props;
        const finalOnClose = submitting ? undefined : onClose;
        const nextButton = (
          <Button type="submit" color="primary">
            next
          </Button>
        );
        const backButton = <Button onClick={setStep(step - 1)}>back</Button>;
        const hasMoreSteps = !(!steps || step + 1 === _.size(steps));
        return (
          <BasicDialog
            PaperProps={{component: ('form' as unknown) as React.ElementType<PaperProps>, onSubmit: handleSubmit}}
            id={`cla-form-${stringToDashed(formName)}`}
            content={
              <React.Fragment>
                {content ? (
                  content
                ) : (
                  <React.Fragment>
                    {steps ? (
                      <Stepper activeStep={step}>
                        {_.map(steps, (step, index) => (
                          <Step index={index} key={index}>
                            <StepLabel>{step.name}</StepLabel>
                          </Step>
                        ))}
                      </Stepper>
                    ) : (
                      undefined
                    )}
                    <FormFields
                      showErrors={submitFailed}
                      fields={_.isArray(fields || []) ? fields : fields[steps[step].name]}
                    />
                  </React.Fragment>
                )}
                <Alert onClose={clearSubmitErrors}>{error}</Alert>
              </React.Fragment>
            }
            actions={
              actions ? (
                <React.Fragment>
                  {step > 0 ? backButton : undefined}
                  {hasMoreSteps
                    ? nextButton
                    : React.cloneElement(actions, {
                        handleSubmit,
                        disabled: submitting,
                      })}
                </React.Fragment>
              ) : (
                undefined
              )
            }
            cancel={!disableExit}
            onClose={disableExit ? undefined : finalOnClose}
            onExited={onExited}
            title={title}
            open={open}
            text={text}
            anchorEl={anchorEl}
            fullWidth={fullWidth}
            maxWidth={maxWidth}
          />
        );
      }
    }

    this.Wrapped = reduxForm<FormData, WrappedComponentInternalProps & FormDialogProps<FormData>>({
      form: formName,
    })(Inner);
  }

  setStep = step => () => {
    this.setState({step});
  };

  render() {
    const {formRef, ...props} = this.props;
    const {step} = this.state;
    const {fields, steps, onClose} = this.props;
    const Inner = this.Wrapped;
    const requiredFields = _.filter(
      fields as Fields<FormData>,
      (field: ExtendedWrappedFieldExternalProps<FormData>) => !field.optional
    );
    let validate;
    if (requiredFields && _.isArray(fields)) {
      validate = createValidateRequired(requiredFields);
    } else if (props.steps) {
      validate = steps[step].validate;
      if (validate === undefined) {
        // null is used to prevent validation for step.
        validate = createValidateRequired(requiredFields[steps[step].name]);
      }
    }
    const hasMoreSteps = !(!steps || step + 1 === _.size(steps));
    if (hasMoreSteps) {
      props.onSubmit = this.setStep(step + 1);
    } else {
      props.onSubmitSuccess = onClose;
    }
    return (
      <Inner
        hasMoreSteps={hasMoreSteps}
        setStep={this.setStep}
        {...this.state}
        validate={validate}
        ref={formRef}
        onSubmit={hasMoreSteps ? this.setStep(step + 1) : undefined}
        {...props}
      />
    );
  }
}
